import { createFileRoute, Link, notFound } from '@tanstack/react-router';
import { Section } from '../components/Section.js';
import { usePageTitle } from '../components/usePageTitle.js';
import { AdrMarkdown } from '../components/AdrMarkdown.js';
import { ADRS, findAdr, type PublishedAdr } from '../lib/adrs.js';

export const Route = createFileRoute('/adrs/$slug')({
  loader: ({ params }) => {
    const adr = findAdr(params.slug);
    if (!adr) throw notFound();
    return adr;
  },
  component: AdrDetailPage,
});

function AdrDetailPage() {
  const adr = Route.useLoaderData() as PublishedAdr;
  usePageTitle(`ADR-${adr.number}: ${adr.title}`);

  // Surface the next and previous ADRs (in numeric order) as small
  // footer links — small navigation aid for readers who want to walk
  // the set in order.
  const ordered = [...ADRS].sort((a, b) => a.number.localeCompare(b.number));
  const idx = ordered.findIndex((a) => a.slug === adr.slug);
  const prev = idx > 0 ? ordered[idx - 1] : undefined;
  const next = idx >= 0 && idx < ordered.length - 1 ? ordered[idx + 1] : undefined;

  return (
    <>
      {/* HERO */}
      <section className="bg-brand-cream">
        <div className="mx-auto max-w-4xl px-6 pb-12 pt-14 sm:px-12 sm:pt-20">
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center rounded-full bg-brand-cream-shadow px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand-sage ring-1 ring-brand-sage/15">
              ADR-{adr.number}
            </span>
            <span className="inline-flex items-center rounded-full bg-brand-sage/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand-sage">
              {adr.status}
            </span>
          </div>
          <h1 className="mt-6 text-4xl font-extrabold leading-[1.1] tracking-tightest text-brand-ink-soft sm:text-5xl">
            {adr.title}
          </h1>
          <p className="mt-6 text-base text-brand-ink-soft/70">
            <Link
              to="/adrs"
              className="text-brand-sage underline-offset-2 hover:underline"
            >
              All design notes
            </Link>
          </p>
        </div>
      </section>

      {/* BODY */}
      <Section tone="cream">
        <article className="mx-auto max-w-3xl">
          <AdrMarkdown source={adr.markdown} />
        </article>
      </Section>

      {/* PREV / NEXT */}
      {(prev || next) && (
        <Section tone="shadow">
          <div className="mx-auto max-w-3xl">
            <div className="grid gap-5 sm:grid-cols-2">
              {prev ? (
                <Link
                  to="/adrs/$slug"
                  params={{ slug: prev.slug }}
                  className="block rounded-2xl border border-brand-sage/15 bg-brand-cream p-6 shadow-brand transition hover:border-brand-sage/40"
                >
                  <p className="text-xs font-semibold uppercase tracking-widest text-brand-sage">
                    Previous &mdash; ADR-{prev.number}
                  </p>
                  <p className="mt-2 text-base font-semibold text-brand-ink-soft">
                    {prev.title}
                  </p>
                </Link>
              ) : (
                <div />
              )}
              {next ? (
                <Link
                  to="/adrs/$slug"
                  params={{ slug: next.slug }}
                  className="block rounded-2xl border border-brand-sage/15 bg-brand-cream p-6 shadow-brand transition hover:border-brand-sage/40 sm:text-right"
                >
                  <p className="text-xs font-semibold uppercase tracking-widest text-brand-sage">
                    Next &mdash; ADR-{next.number}
                  </p>
                  <p className="mt-2 text-base font-semibold text-brand-ink-soft">
                    {next.title}
                  </p>
                </Link>
              ) : (
                <div />
              )}
            </div>
          </div>
        </Section>
      )}

      {/* CTA */}
      <section className="bg-brand-ink text-brand-cream">
        <div className="mx-auto max-w-6xl px-6 py-20 sm:px-12 sm:py-24">
          <h2 className="text-3xl font-extrabold tracking-tight sm:text-5xl">
            Questions on this one?
          </h2>
          <p className="mt-6 max-w-2xl text-lg text-brand-cream/80">
            If you&rsquo;ve hit the same problem and landed somewhere
            different, we&rsquo;d genuinely like to hear about it.{' '}
            <Link
              to="/contact"
              className="font-medium underline-offset-4 hover:underline"
            >
              Get in touch
            </Link>
            .
          </p>
        </div>
      </section>
    </>
  );
}
